import { Cache } from './cache/Cache';
import {
  CacheParams,
  createPersistedCache,
} from './cache/createPersistedCache';
import { defaultToCacheKey } from './quickLoad';
import { makeLoadFailed } from './utils/makeLoadFailed';

export function wrapPromise<
  TFetcher extends (...args: any[]) => Promise<any>,
  TToCacheKey extends (...args: Parameters<TFetcher>) => string,
>(
  cacheConfig: CacheParams<ReturnType<TFetcher>> & {
    fetch: TFetcher;
    toCacheKey?: TToCacheKey;
  }
) {
  const cache = createPersistedCache<ReturnType<TFetcher>>(cacheConfig);

  return wrapPromiseWithCache({
    fetch: cacheConfig.fetch,
    toCacheKey: cacheConfig.toCacheKey,
    cache,
  });
}

type ReadThroughOpts<TCache extends Cache<any, any>> = Parameters<
  TCache['readThrough']
>[0]['opts'];

export function wrapPromiseWithCache<
  TFetcher extends (...args: any[]) => Promise<any>,
  TToCacheKey extends (...args: Parameters<TFetcher>) => string,
  TCache extends ReturnType<typeof createPersistedCache<ReturnType<TFetcher>>>,
>({
  fetch,
  cache,
  toCacheKey = defaultToCacheKey as TToCacheKey,
}: {
  fetch: TFetcher;
  cache: TCache;
  toCacheKey?: TToCacheKey;
}) {
  return {
    makeFetchWithOpts:
      (opts?: ReadThroughOpts<TCache>) =>
      (...args: Parameters<TFetcher>) =>
        cache.readThrough({
          cacheKey: toCacheKey(...args),
          fetchValue: () => fetch(...args) as ReturnType<TFetcher>,
          opts,
        }) ?? makeLoadFailed<ReturnType<TFetcher>>(),
    syncIntoCache: (
      value: ReturnType<TFetcher>,
      ...args: Parameters<TFetcher>
    ) => {
      cache.set(toCacheKey(...args), value, true);
    },
    debug: cache.printDebug,
    clearCache: cache.clear,
  };
}
